import {
  DEFAULT_ARTICLE_VIEWS,
  DENSITIES,
  MAX_ME_LINKS,
  READING_SIZES,
  READING_WIDTHS,
  SHARE_VISIBILITIES,
  SORT_ORDERS,
  VIEW_MODES,
  type Density,
  type Settings,
  type ShareVisibility,
  type UpdateProfileInput,
} from '@rss/shared';
import { ChevronLeft, Download, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { OpmlImportDescription, OpmlImportPanel } from '@/components/feed/OpmlImport';
import { RulesSection } from '@/components/rules/RulesSection';
import { AppShell } from '@/components/layout/AppShell';
import { ThemeTiles } from '@/components/theme/ThemePicker';
import { Button } from '@/components/ui/button';
import { notify } from '@/lib/notify';
import { ApiRequestError } from '@/lib/api';
import { ARTICLE_VIEW_LABELS } from '@/lib/article-view';
import { useChangePassword, useSession, useUpdateAccount } from '@/lib/auth';
import { useResetViews } from '@/lib/folders';
import { identityInput, useProfile, useUpdateProfile } from '@/lib/profile';
import { useInstallPrompt } from '@/lib/pwa';
import { READING_SIZE_LABELS, READING_WIDTH_LABELS } from '@/lib/reading-format';
import { useSettings } from '@/lib/settings';
import { SORT_LABELS } from '@/lib/sort-order';
import { cn } from '@/lib/utils';
import { VIEW_LABELS } from '@/lib/view-labels';

const inputClass =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiRequestError ? (err.body?.message ?? err.message) : fallback;
}

/** Profile (display name + email) and password change (SPEC-017). */
function AccountSection() {
  const { data: user } = useSession();
  const updateAccount = useUpdateAccount();
  const changePassword = useChangePassword();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [profileMsg, setProfileMsg] = useState<string | null>(null);

  // Seed the profile fields once the session user is available.
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
      setEmail(user.email);
    }
  }, [user]);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);

  function saveProfile(e: FormEvent) {
    e.preventDefault();
    setProfileMsg(null);
    const patch: { displayName?: string; email?: string } = {};
    if (user && displayName !== user.displayName) patch.displayName = displayName;
    if (user && email !== user.email) patch.email = email;
    if (!patch.displayName && !patch.email) {
      setProfileMsg('Nothing to save.');
      return;
    }
    updateAccount.mutate(patch, {
      onSuccess: () => setProfileMsg('Saved.'),
      onError: (err) => setProfileMsg(errorMessage(err, 'Could not save.')),
    });
  }

  const pwTooShort = next.length > 0 && next.length < 8;
  const pwMismatch = confirm.length > 0 && next !== confirm;
  const pwValid = current.length > 0 && next.length >= 8 && next === confirm;

  function savePassword(e: FormEvent) {
    e.preventDefault();
    setPwMsg(null);
    changePassword.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          setPwMsg('Password changed. Other devices have been signed out.');
          setCurrent('');
          setNext('');
          setConfirm('');
        },
        onError: (err) => setPwMsg(errorMessage(err, 'Could not change password.')),
      },
    );
  }

  return (
    <section className="space-y-5 rounded-lg border p-4">
      <h2 className="font-medium">Account</h2>

      <form onSubmit={saveProfile} className="space-y-3">
        <label className="block space-y-1">
          <span className="text-sm">Display name</span>
          <input
            className={inputClass}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={64}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm">Email</span>
          <input
            type="email"
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
        {user && (
          <p className="text-xs text-muted-foreground">
            Signed in as <span className="font-medium">@{user.username}</span> (username can&apos;t
            be changed).
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={updateAccount.isPending}>
            {updateAccount.isPending ? 'Saving…' : 'Save profile'}
          </Button>
          {profileMsg && (
            <span
              className={cn(
                'text-sm',
                updateAccount.isError ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {profileMsg}
            </span>
          )}
        </div>
      </form>

      <form onSubmit={savePassword} className="space-y-3 border-t pt-4">
        <h3 className="text-sm font-medium">Change password</h3>
        <input
          type="password"
          className={inputClass}
          placeholder="Current password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
        />
        <input
          type="password"
          className={inputClass}
          placeholder="New password (min 8 characters)"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
        />
        <input
          type="password"
          className={inputClass}
          placeholder="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
        {pwTooShort && <p className="text-xs text-destructive">Use at least 8 characters.</p>}
        {pwMismatch && <p className="text-xs text-destructive">Passwords do not match.</p>}
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!pwValid || changePassword.isPending}>
            {changePassword.isPending ? 'Changing…' : 'Change password'}
          </Button>
          {pwMsg && (
            <span
              className={cn(
                'text-sm',
                changePassword.isError ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {pwMsg}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

const DENSITY_LABEL: Record<Density, string> = {
  comfortable: 'Comfortable',
  compact: 'Compact',
};

/** A labeled segmented control for an enum setting. */
function Segmented<T extends string>({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm">{label}</span>
      <div className="inline-flex rounded-md border p-0.5">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            aria-pressed={value === opt}
            onClick={() => onChange(opt)}
            className={cn(
              'rounded px-3 py-1 text-sm transition-colors duration-150 motion-reduce:transition-none',
              value === opt
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {labels[opt]}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span>
        <span className="block text-sm">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 shrink-0 accent-primary"
      />
    </label>
  );
}

/**
 * The view switcher saves a layout per feed and per folder (and the default on
 * All items), so saved layouts pile up. This puts them all back on the default.
 */
function ResetViewsRow() {
  const reset = useResetViews();
  const onReset = () => {
    if (!confirm('Reset every feed and folder to the default list view?')) return;
    reset.mutate(undefined, {
      onSuccess: () => notify.success('All feeds and folders now use the default list view.'),
    });
  };
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="block text-xs text-muted-foreground">
        The view switcher saves a view for the feed or folder you are on. On All items, it changes
        this default.
      </span>
      <Button size="sm" variant="outline" onClick={onReset} disabled={reset.isPending}>
        {reset.isPending ? 'Resetting…' : 'Reset feed and folder views'}
      </Button>
    </div>
  );
}

const VISIBILITY_LABEL: Record<ShareVisibility, string> = {
  off: 'Off',
  instance: 'This instance',
  public: 'Public web',
};

const VISIBILITY_HINT: Record<ShareVisibility, string> = {
  off: 'Shared items are a private list only you can see.',
  instance: 'Other users of this instance see your shares in their Community view.',
  public: 'Anyone on the web can read your shared items page and subscribe to its feed.',
};

/** Sharing profile (SPEC-019): visibility, slug, and the public page fields.
 *  The switches save at once, as in Preferences. The text fields wait for
 *  their Save button (#44). */
/**
 * How to get a verified link on Mastodon (SPEC-026). The rel="me" links show
 * only on a public page, so the hint names that page, or says to turn one on.
 */
function VerifyHint({ pageUrl }: { pageUrl: string | null }) {
  if (!pageUrl) {
    return (
      <p className="text-xs text-muted-foreground">
        These links show only on a public page. Turn on public shares or the public blogroll
        first.
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground">
      To show this page as verified on Mastodon, add its address,{' '}
      <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
        {pageUrl.replace(/^https?:\/\//, '')}
      </a>
      , to your Mastodon profile, and add your Mastodon profile to Other profiles.
    </p>
  );
}

export function SharingSection() {
  const { data: profile } = useProfile();
  const update = useUpdateProfile();
  // A second instance, so a switch never makes the Save button say "Saving…".
  const updateSwitch = useUpdateProfile();
  const [visibility, setVisibility] = useState<ShareVisibility>('off');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [blogrollEnabled, setBlogrollEnabled] = useState(false);
  // IndieWeb identity (SPEC-026). The links box holds one address per line.
  const [website, setWebsite] = useState('');
  const [photo, setPhoto] = useState('');
  const [links, setLinks] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Seed the form once from the loaded profile (or its server suggestion).
  const seeded = useRef(false);
  useEffect(() => {
    if (!profile || seeded.current) return;
    seeded.current = true;
    setVisibility(profile.visibility);
    setSlug(profile.slug);
    setTitle(profile.title ?? '');
    setBio(profile.bio ?? '');
    setBlogrollEnabled(profile.blogrollEnabled);
    setWebsite(profile.websiteUrl ?? '');
    setPhoto(profile.photoUrl ?? '');
    setLinks(profile.meLinks.join('\n'));
  }, [profile]);

  // Show the new value at once. If the save fails, go back to the old one.
  function saveNow(patch: Pick<UpdateProfileInput, 'visibility' | 'blogrollEnabled'>, done: string, revert: () => void) {
    setError(null);
    updateSwitch.mutate(patch, {
      onSuccess: () => notify.success(done),
      onError: (err) => {
        revert();
        setError(errorMessage(err, 'Could not save sharing settings'));
      },
    });
  }

  function onVisibility(next: ShareVisibility) {
    const before = visibility;
    setVisibility(next);
    saveNow({ visibility: next }, `Who can see your shares: ${VISIBILITY_LABEL[next]}.`, () =>
      setVisibility(before),
    );
  }

  function onBlogroll(next: boolean) {
    setBlogrollEnabled(next);
    saveNow(
      { blogrollEnabled: next },
      next ? 'Public blogroll on.' : 'Public blogroll off.',
      () => setBlogrollEnabled(!next),
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const identity = identityInput({ website, photo, links });
    if ('error' in identity) {
      setError(identity.error);
      return;
    }
    update.mutate(
      {
        slug: slug.trim(),
        title: title.trim() || null,
        bio: bio.trim() || null,
        ...identity.input,
      },
      {
        onSuccess: () => notify.success('Page details saved.'),
        onError: (err) => setError(errorMessage(err, 'Could not save sharing settings')),
      },
    );
  }

  return (
    <section id="sharing" className="space-y-4 rounded-lg border p-4">
      <h2 className="font-medium">Sharing</h2>
      <p className="text-xs text-muted-foreground">
        Mark articles as shared (the share menu on any article, or{' '}
        <kbd className="rounded border bg-muted px-1 font-mono">S</kbd>) and choose who can see
        them.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1">
          <Segmented
            label="Who can see your shares"
            value={visibility}
            options={SHARE_VISIBILITIES}
            labels={VISIBILITY_LABEL}
            onChange={onVisibility}
          />
          <p className="text-xs text-muted-foreground">{VISIBILITY_HINT[visibility]}</p>
        </div>

        <Toggle
          label="Public blogroll"
          hint="Publishes the feeds you follow (and your folder names) as a page + OPML. Exclude individual feeds from each feed's settings."
          checked={blogrollEnabled}
          onChange={onBlogroll}
        />
        {profile?.blogrollUrl && (
          <p className="text-xs text-muted-foreground">
            Your blogroll:{' '}
            <a
              href={profile.blogrollUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {profile.blogrollUrl.replace(/^https?:\/\//, '')}
            </a>
          </p>
        )}

        <label className="block space-y-1">
          <span className="text-sm">Page address</span>
          <div className="flex items-center gap-1">
            <span className="shrink-0 text-sm text-muted-foreground">/u/</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className={inputClass}
              aria-label="Public page address"
              minLength={3}
              maxLength={32}
              pattern="[a-z0-9][a-z0-9-]{1,30}[a-z0-9]"
              title="3-32 lowercase letters, numbers, and dashes"
            />
          </div>
        </label>

        <label className="block space-y-1">
          <span className="text-sm">Page title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
            maxLength={80}
            placeholder="Defaults to your name's shared items"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm">Bio</span>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={2}
            maxLength={500}
            className={cn(inputClass, 'h-auto resize-y py-2')}
            placeholder="A line about you or what you share (optional)"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm">Your website</span>
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className={inputClass}
            maxLength={300}
            placeholder="https://example.com"
          />
        </label>

        {/* The hints sit outside the labels, so they describe the field
            (aria-describedby) and are not read as part of its name. */}
        <div className="space-y-1">
          <label className="block space-y-1">
            <span className="text-sm">Photo</span>
            <input
              type="url"
              value={photo}
              onChange={(e) => setPhoto(e.target.value)}
              className={inputClass}
              maxLength={300}
              placeholder="https://example.com/me.jpg"
              aria-describedby="photo-hint"
            />
          </label>
          <p id="photo-hint" className="text-xs text-muted-foreground">
            An https address of a square image. It is shown on your public pages.
          </p>
        </div>

        <div className="space-y-1">
          <label className="block space-y-1">
            <span className="text-sm">Other profiles</span>
            <textarea
              value={links}
              onChange={(e) => setLinks(e.target.value)}
              rows={3}
              className={cn(inputClass, 'h-auto resize-y py-2')}
              placeholder={'https://mastodon.social/@you\nhttps://github.com/you'}
              aria-describedby="links-hint"
            />
          </label>
          <p id="links-hint" className="text-xs text-muted-foreground">
            Mastodon, GitHub, or any profile that is you. One address per line, up to{' '}
            {MAX_ME_LINKS}.
          </p>
        </div>

        <VerifyHint pageUrl={profile?.shareUrl ?? profile?.blogrollUrl ?? null} />

        {error &&<p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save page details'}
          </Button>
          {profile?.shareUrl && (
            <a
              href={profile.shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              Open your page
            </a>
          )}
        </div>
        {profile?.shareUrl && (
          <p className="text-xs text-muted-foreground">
            Your page is also an Atom and JSON feed, so other readers can subscribe to your
            shares at {profile.shareUrl}/feed.xml
          </p>
        )}
      </form>
    </section>
  );
}

export function SettingsPage() {
  const { settings, update } = useSettings();
  const { canInstall, promptInstall } = useInstallPrompt();
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => update({ [key]: value });

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl space-y-6 p-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ChevronLeft className="size-4" /> Back
            </Link>
          </Button>
          <h1 className="text-xl font-semibold">Settings</h1>
        </div>

        <AccountSection />

        <SharingSection />

        <section className="space-y-4 rounded-lg border p-4">
          <h2 className="font-medium">Preferences</h2>
          <div className="space-y-2">
            <span className="text-sm">Theme</span>
            <ThemeTiles />
          </div>
          <Segmented
            label="Default list view"
            value={settings.defaultViewMode}
            options={VIEW_MODES}
            labels={VIEW_LABELS}
            onChange={(v) => set('defaultViewMode', v)}
          />
          <ResetViewsRow />
          <Segmented
            label="Default article order"
            value={settings.defaultSortOrder}
            options={SORT_ORDERS}
            labels={SORT_LABELS}
            onChange={(v) => set('defaultSortOrder', v)}
          />
          <Segmented
            label="Density"
            value={settings.density}
            options={DENSITIES}
            labels={DENSITY_LABEL}
            onChange={(v) => set('density', v)}
          />
          <Segmented
            label="Default article view"
            value={settings.defaultArticleView}
            options={DEFAULT_ARTICLE_VIEWS}
            labels={ARTICLE_VIEW_LABELS}
            onChange={(v) => set('defaultArticleView', v)}
          />
          <Segmented
            label="Article text size"
            value={settings.readingSize}
            options={READING_SIZES}
            labels={READING_SIZE_LABELS}
            onChange={(v) => set('readingSize', v)}
          />
          <Segmented
            label="Article line width"
            value={settings.readingWidth}
            options={READING_WIDTHS}
            labels={READING_WIDTH_LABELS}
            onChange={(v) => set('readingWidth', v)}
          />
          <Toggle
            label="Mark read when opened"
            hint="Off: articles stay unread until you mark them (the envelope button, m, or u)."
            checked={settings.markReadOnOpen}
            onChange={(v) => set('markReadOnOpen', v)}
          />
          <Toggle
            label="Mark read on scroll"
            hint="Mark an article read when it scrolls off the top of the list."
            checked={settings.markReadOnScroll}
            onChange={(v) => set('markReadOnScroll', v)}
          />
          <Toggle
            label="Show unread only"
            hint="Hide read articles, and feeds with nothing unread. The same as the toolbar button."
            checked={settings.showUnreadOnly}
            onChange={(v) => set('showUnreadOnly', v)}
          />
        </section>

        <RulesSection />

        {canInstall && (
          <section className="rounded-lg border p-4">
            <h2 className="font-medium">Install app</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Add Reader to your home screen to launch it full-screen and keep already-read
              articles available offline.
            </p>
            <Button className="mt-3" onClick={promptInstall}>
              <Smartphone className="size-4" /> Install Mood Reader
            </Button>
          </section>
        )}

        <section className="rounded-lg border p-4">
          <h2 className="font-medium">Import subscriptions</h2>
          <OpmlImportDescription className="mt-1 text-sm text-muted-foreground" />
          <div className="mt-3">
            <OpmlImportPanel />
          </div>
        </section>

        <section className="rounded-lg border p-4">
          <h2 className="font-medium">Export subscriptions</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Download your folders and feeds as an OPML file any reader can import.
          </p>
          <Button variant="outline" className="mt-3" asChild>
            {/* A plain GET with Content-Disposition; the browser handles the save. */}
            <a href="/api/opml/export" download="reader-subscriptions.opml">
              <Download className="size-4" /> Download OPML
            </a>
          </Button>
        </section>
      </div>
    </AppShell>
  );
}
